import { useQuery, useMutation, useQueryClient, UseQueryResult, UseMutationResult } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
  listAll,
  createDestination as createDestinationAPI,
  deleteDestination as deleteDestinationAPI,
  updateDestination as updateDestinationAPI,
} from '_/api/destinations/destinations.api';
import { IDestination } from '_/types/destinations/destinations.types';
import { notification } from 'antd';

export const DESTINATIONS_QUERY_KEY = ['destinations'];

export const useDestinations = (): UseQueryResult<IDestination[]> => {
  return useQuery({
    queryKey: DESTINATIONS_QUERY_KEY,
    queryFn: () => listAll(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useDestination = (destinationName?: string) => {
  const { data: destinations = [], isLoading } = useDestinations();

  const destination = destinations.find((d) => d.name === destinationName);

  // Process encrypted config to match Redux selector behavior
  const processedDestination = destination
    ? {
        ...destination,
        encryptedConfig: Object.fromEntries(
          Object.entries(destination.encryptedConfig || {})
            .filter(([, value]) => !!value)
            .map(([key]) => [key, '']),
        ),
      }
    : null;

  return {
    data: processedDestination,
    isLoading: isLoading,
  };
};

export const useUpdateDestination = (
  orgSlug: string,
): UseMutationResult<any, Error, { destinationId: string; destination: any }> => {
  const queryClient = useQueryClient();
  const history = useNavigate();

  return useMutation({
    mutationFn: ({ destinationId, destination }: { destinationId: string; destination: any }) =>
      updateDestinationAPI(destinationId, destination),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: DESTINATIONS_QUERY_KEY });
      notification.success({
        message: 'Destination Updated',
      });
      void history({ to: `/orgs/${orgSlug}/destinations` });
    },
    onError: (error: any) => {
      console.error('Error updating destination', error);
      // Don't show snackbar here - let the component handle field-specific errors
      throw error;
    },
  });
};

export const useDeleteDestination = (): UseMutationResult<any, Error, string> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (destinationId: string) => deleteDestinationAPI(destinationId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: DESTINATIONS_QUERY_KEY });
      notification.success({
        message: 'Destination Deleted',
      });
    },
    onError: (error: any) => {
      console.error('Error deleting destination', error);
      notification.error({
        message: error.messages?.[0] || 'Error deleting destination',
      });
    },
  });
};

export const useCreateDestination = (orgSlug: string): UseMutationResult<any, Error, any> => {
  const queryClient = useQueryClient();
  const history = useNavigate();

  return useMutation({
    mutationFn: (destination: any) => createDestinationAPI(destination),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: DESTINATIONS_QUERY_KEY });
      notification.success({
        message: 'Destination Saved',
      });
      void history({ to: `/orgs/${orgSlug}/destinations` });
    },
    onError: (error: any) => {
      console.error('Error creating destination', error);
      // Don't show snackbar here - let the component handle field-specific errors
      throw error;
    },
  });
};
